import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { useAuth } from './AuthProvider'
import { inboxHref } from './routes'
import { Header } from '../shell/Header'
import { CostStrip } from '../shell/CostStrip'
import { SideNav } from '../shell/SideNav'
import { MobileTabBar } from '../shell/MobileTabBar'
import css from '../shell/shell.module.css'
import { SignIn } from '../views/SignIn'
import { Lanes } from '../views/Lanes'
import { Status } from '../views/Status'
import { Merge } from '../views/Merge'
import { Approvals } from '../views/Approvals'
import { Detail } from '../views/Detail'
import { Activity } from '../views/Activity'
import { Policy } from '../views/Policy'
import { Costs } from '../views/Costs'
import { Changelog } from '../views/Changelog'
import { Directory } from '../views/Directory'
import { Desk } from '../views/Desk'
import { Reports } from '../views/Reports'
import { Runbook } from '../views/Runbook'

/**
 * One element for every old name the inbox has had, with or without an item.
 * It reads the id and the search off the location rather than being given a
 * literal path, so `?source=` and `?dept=` survive the hop.
 */
function LegacyInboxRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  return <Navigate to={inboxHref(id, search)} replace />
}

function Shell() {
  return (
    <>
      <Header />
      <CostStrip />
      <div className={css.body}>
        <SideNav />
        <main className={css.main}>
          <Outlet />
        </main>
      </div>
      <MobileTabBar />
    </>
  )
}

export function App() {
  const { session, ready } = useAuth()

  // Nothing renders until the stored session has been restored, so a reload does
  // not flash the sign-in screen at someone who is already signed in.
  if (!ready) return null
  if (!session) return <SignIn />

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/lanes" element={<Lanes />} />
        {/* Is anything broken or waiting on me, across every automated job.
            One read of fleet_status; no live call to anything it reports on. */}
        <Route path="/status" element={<Status />} />
        {/* What can be merged by hand right now. One read of merge_status; the
            judgement was made on the box, and this page links out to GitHub. */}
        <Route path="/merge" element={<Merge />} />
        <Route path="/inbox" element={<Approvals />} />
        <Route path="/inbox/:id" element={<Detail />} />
        {/* The view was Approvals until it was reframed as triage, and Queue
            until it was renamed Inbox on 23 Sep. Links were bookmarked and
            pasted to colleagues under both, so they keep working rather than
            landing on the lanes board. */}
        <Route path="/queue" element={<LegacyInboxRedirect />} />
        <Route path="/queue/:id" element={<LegacyInboxRedirect />} />
        <Route path="/approvals" element={<LegacyInboxRedirect />} />
        <Route path="/approvals/:id" element={<LegacyInboxRedirect />} />
        {/* One desk per department, and the slug is data: any department the
            table carries -- or one it does not -- resolves here. */}
        <Route path="/desk/:slug" element={<Desk />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="/policy" element={<Policy />} />
        <Route path="/costs" element={<Costs />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/directory" element={<Directory />} />
        {/* The shelf for reports, starting with how the fleet's apps are used.
            Every report on it is addressable by its search params, so a link
            opens the one someone was sent. */}
        <Route path="/reports" element={<Reports />} />
        <Route path="/runbook" element={<Runbook />} />
        <Route path="*" element={<Navigate to="/lanes" replace />} />
      </Route>
    </Routes>
  )
}
